import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AdminShell } from '../../components/AdminShell';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  SUBDOMAIN_RE,
  STEPS_FULL,
  STEPS_ASSISTED,
  genPw,
} from '../../lib/onboardShop/helpers';
import { StepShop } from '../../components/onboardShop/StepShop';
import { StepOwner } from '../../components/onboardShop/StepOwner';
import { StepBarbers } from '../../components/onboardShop/StepBarbers';
import { StepServices, StepHours, StepStripe } from '../../components/onboardShop/StepMisc';
import { StepAssistedSend, StepReview } from '../../components/onboardShop/StepReview';
import { CredentialsSheet } from '../../components/onboardShop/CredentialsSheet';

/**
 * Flow 1 — Admin end-to-end onboarding wizard.
 * NO emails. NO SMS. Admin sets every password. On finish, POSTs one
 * atomic /api/onboarding/admin-full and shows the copyable credentials.
 *
 * Sub-components live under `components/onboardShop/` — this container
 * only owns state, validation, and the Next/Back/Submit flow.
 */
const OnboardShopInner = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('full');   // 'full' | 'assisted'
  const [step, setStep] = useState(0);

  const STEPS = mode === 'assisted' ? STEPS_ASSISTED : STEPS_FULL;

  const [shop, setShop] = useState({
    name: '', subdomain: '', plan: 'standard', interval: 'monthly',
    accent_colour: '#4f46ff', welcome_text: '',
  });
  const [subStatus, setSubStatus] = useState('idle');
  const [owner, setOwner] = useState({ email: '', password: genPw(), name: '', phone: '' });
  const [showOwnerPw, setShowOwnerPw] = useState(false);
  const [barbers, setBarbers] = useState([]);
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const switchMode = (m) => {
    if (m === mode) return;
    setMode(m);
    setStep(0);
    setError('');
  };

  const checkSubdomain = async (raw) => {
    const s = (raw || '').trim().toLowerCase();
    if (!s) { setSubStatus('idle'); return; }
    if (!SUBDOMAIN_RE.test(s)) { setSubStatus('invalid'); return; }
    setSubStatus('checking');
    const { data } = await supabase.from('shops').select('id').eq('subdomain', s).limit(1);
    setSubStatus((data && data.length) ? 'taken' : 'ok');
  };

  const stepValid = useMemo(() => {
    switch (STEPS[step].key) {
      case 'shop':
        return shop.name.trim().length >= 2 && subStatus === 'ok';
      case 'owner': {
        const base = owner.email.includes('@') && owner.password.length >= 8 && owner.name.trim().length >= 2;
        // Phone is captured + required in ALL onboarding flows for
        // support contact.
        return base && owner.phone.trim().length >= 6;
      }
      case 'barbers':
        return barbers.every((b) =>
          b.name.trim().length >= 2 &&
          (!b.giveLogin || (b.email.includes('@') && b.password.length >= 8))
        );
      case 'services':
        return services.every((s) => s.name.trim().length >= 2 && Number(s.price) >= 0 && Number(s.duration_min) > 0);
      case 'hours':
      case 'stripe':
      case 'review':
      default:
        return true;
    }
  }, [step, shop, subStatus, owner, barbers, services, STEPS]);

  const goNext = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const goBack = () => setStep((s) => Math.max(0, s - 1));
  const canJumpTo = (i) => i <= step;

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const url = mode === 'assisted'
        ? `${process.env.REACT_APP_BACKEND_URL}/api/onboarding/assisted-start`
        : `${process.env.REACT_APP_BACKEND_URL}/api/onboarding/admin-full`;
      const basePayload = {
        shop_name: shop.name.trim(),
        subdomain: shop.subdomain.trim().toLowerCase(),
        plan: shop.plan,
        interval: shop.interval,
        accent_colour: shop.accent_colour,
        owner_email: owner.email.trim().toLowerCase(),
        owner_password: owner.password,
        owner_name: owner.name.trim(),
      };
      const payload = mode === 'assisted'
        ? { ...basePayload, owner_phone: owner.phone.trim() }
        : {
            ...basePayload,
            owner_phone: owner.phone.trim(),
            welcome_text: shop.welcome_text,
            services: services.map((s) => ({
              name: s.name.trim(),
              price: Number(s.price),
              duration_min: Number(s.duration_min),
              description: '',
            })),
            barbers: barbers.map((b) => ({
              name: b.name.trim(),
              role: b.role.trim() || null,
              bio: b.bio.trim() || null,
              give_login: b.giveLogin,
              // Always send the email so the backend can auto-link
              // owner-as-barber rows even when giveLogin is false.
              email: (b.email || '').trim().toLowerCase() || null,
              password: b.giveLogin ? b.password : null,
              hours: b.hours.filter((h) => h.is_open).map((h) => ({
                weekday: h.weekday, start_time: h.start_time, end_time: h.end_time, is_open: true,
              })),
            })),
          };
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let json = {}; try { json = JSON.parse(text); } catch (_e) { /* raw */ }
      if (!res.ok) throw new Error(json.detail || text || `HTTP ${res.status}`);
      setResult({ ...json, mode });
    } catch (e) {
      setError(e.message || 'Onboarding failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return <CredentialsSheet result={result} onDone={() => navigate(`/admin/shops/${result.shop_id}`)} />;
  }

  const active = STEPS[step].key;

  return (
    <main className="main admin-page">
      <div className="topbar">
        <div>
          <h2>Onboard a shop</h2>
          <div className="sub">
            {mode === 'full'
              ? 'End-to-end setup. No emails or texts sent — you\u2019ll copy the credentials at the end.'
              : 'Bootstrap the shop, email the credentials to the owner, they finish the rest.'}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }} data-testid="wiz-mode">
          <button className={`btn ${mode === 'full' ? 'p' : 's'}`} onClick={() => switchMode('full')} data-testid="wiz-mode-full">Full setup</button>
          <button className={`btn ${mode === 'assisted' ? 'p' : 's'}`} onClick={() => switchMode('assisted')} data-testid="wiz-mode-assisted">Assisted (email handover)</button>
        </div>
      </div>

      <div className="body">
        <div className="wiz" data-testid="onboard-wizard">
          <ul className="wiz-rail" data-testid="wiz-rail">
            {STEPS.map((s, i) => (
              <li
                key={s.key}
                className={i === step ? 'on' : (i < step ? 'done' : '')}
                onClick={() => canJumpTo(i) && setStep(i)}
                data-testid={`wiz-step-${s.key}`}
              >
                <span className="n">{i < step ? <Check size={12} /> : i + 1}</span>
                {s.label}
              </li>
            ))}
          </ul>

          <div className="panel" style={{ padding: 24 }}>
            {active === 'shop'     && <StepShop shop={shop} setShop={setShop} subStatus={subStatus} checkSubdomain={checkSubdomain} showWelcome={mode === 'full'} />}
            {active === 'owner'    && <StepOwner owner={owner} setOwner={setOwner} showPw={showOwnerPw} setShowPw={setShowOwnerPw} showPhone={true} />}
            {active === 'barbers'  && <StepBarbers barbers={barbers} setBarbers={setBarbers} plan={shop.plan} owner={owner} />}
            {active === 'services' && <StepServices services={services} setServices={setServices} />}
            {active === 'hours'    && <StepHours barbers={barbers} setBarbers={setBarbers} />}
            {active === 'stripe'   && <StepStripe />}
            {active === 'review'   && (
              mode === 'assisted'
                ? <StepAssistedSend shop={shop} owner={owner} />
                : <StepReview shop={shop} owner={owner} barbers={barbers} services={services} />
            )}

            {error && <div className="modal-error" style={{ marginTop: 12 }} data-testid="wiz-error">{error}</div>}

            <div className="wiz-foot">
              <button className="btn s" onClick={goBack} disabled={step === 0 || submitting} data-testid="wiz-back">
                <ChevronLeft size={14} /> Back
              </button>
              <div className="rside">
                {active !== 'review' && (
                  <button className="btn p" onClick={goNext} disabled={!stepValid || submitting} data-testid="wiz-next">
                    Next <ChevronRight size={14} />
                  </button>
                )}
                {active === 'review' && (
                  <button className="btn p" onClick={submit} disabled={submitting} data-testid="wiz-submit">
                    {submitting
                      ? (mode === 'assisted' ? 'Sending\u2026' : 'Creating\u2026')
                      : (mode === 'assisted' ? 'Send handover email' : 'Finish onboarding')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

const OnboardShop = () => <AdminShell><OnboardShopInner /></AdminShell>;
export default OnboardShop;
